import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { 
  Plus, Pencil, Trash2, Search, Building2, Mail, Phone, 
  Truck, ShoppingCart, Image, ToggleLeft, ToggleRight,
  Euro, FileText, Upload, User, Briefcase, Gamepad2, Settings2,
  Globe, Clock, PackageCheck, AlertTriangle, MapPin, ExternalLink
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import {
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import api from '../../lib/api';
import PartnerBadges, { RosetteIcon } from '../../components/PartnerBadges';
import { isPartnerClickable, getPartnerAccessLabel, PARTNER_LABEL_ACTIVE, PARTNER_LABEL_INACTIVE } from '../../lib/partnerPolicy';
import GuardedDialog from '../../components/GuardedDialog';
import useFormDirtyState from '../../hooks/useFormDirtyState';

/**
 * AdminPartners - Gestion des partenaires (Admin)
 */

const ZONE_OPTIONS = [
  { value: 'france_metropolitaine', label: 'France m\u00e9tropolitaine' },
  { value: 'corse', label: 'Corse' },
  { value: 'dom_tom', label: 'DOM-TOM' },
  { value: 'belgique', label: 'Belgique' },
  { value: 'luxembourg', label: 'Luxembourg' },
  { value: 'suisse', label: 'Suisse' },
  { value: 'allemagne', label: 'Allemagne' },
  { value: 'espagne', label: 'Espagne' },
  { value: 'italie', label: 'Italie' },
  { value: 'pays_bas', label: 'Pays-Bas' },
  { value: 'europe_autre', label: 'Europe (autre)' },
];

const AdminPartners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const { hasUnsavedChanges, setDirty, resetDirty } = useFormDirtyState();
  
  const [formData, setFormData] = useState({
    nom: '',
    slug: '',
    description: '',
    logo: '',
    banniere: '',
    conditions_achat: '',
    contacts: [],
    contact_email: '',
    contact_telephone: '',
    process_commande: '',
    franco: '',
    minimum_commande: '',
    delai_paiement: '',
    frais_port: '',
    is_active: true,
    supplier_type: 'other',
    is_partner: false,
    capabilities: [],
    // V5 — Structured commercial rules
    shipping_rules: [],
    payment_terms: { delai_jours: '', escompte_percent: '', encours_max: '', mode_paiement: '', commentaire: '' },
    precommandes_autorisees: false,
    rupture_commandable: false,
    livraison_partielle: false,
  });

  useEffect(() => {
    loadPartners();
  }, []);

  const loadPartners = async () => {
    try {
      const response = await api.get('/partners?include_inactive=true');
      setPartners(response.data);
    } catch (error) {
      console.error('Error loading partners:', error);
      toast.error('Erreur lors du chargement des partenaires');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (partner = null) => {
    if (partner) {
      setEditingPartner(partner);
      
      // Initialiser les contacts
      let contacts = partner.contacts || [];
      if (contacts.length === 0 && (partner.contact_email || partner.contact_telephone)) {
        contacts = [{
          id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
          nom: 'Contact principal',
          email: partner.contact_email || '',
          telephone: partner.contact_telephone || '',
          fonction: ''
        }];
      }
      
      setFormData({
        nom: partner.nom || '',
        slug: partner.slug || '',
        description: partner.description || '',
        logo: partner.logo || '',
        banniere: partner.banniere || '',
        conditions_achat: partner.conditions_achat || '',
        contacts: contacts,
        contact_email: partner.contact_email || '',
        contact_telephone: partner.contact_telephone || '',
        process_commande: partner.process_commande || '',
        franco: partner.franco || '',
        minimum_commande: partner.minimum_commande || '',
        delai_paiement: partner.delai_paiement || '',
        frais_port: partner.frais_port || '',
        is_active: partner.is_active !== false,
        supplier_type: partner.supplier_type || 'other',
        is_partner: partner.is_partner === true,
        capabilities: partner.capabilities || [],
        // V5 fields
        shipping_rules: partner.shipping_rules || [],
        payment_terms: partner.payment_terms || { delai_jours: '', escompte_percent: '', encours_max: '' },
        precommandes_autorisees: partner.precommandes_autorisees === true,
        rupture_commandable: partner.rupture_commandable === true,
        livraison_partielle: partner.livraison_partielle === true,
      });
    } else {
      setEditingPartner(null);
      setFormData({
        nom: '',
        slug: '',
        description: '',
        logo: '',
        banniere: '',
        conditions_achat: '',
        contacts: [],
        contact_email: '',
        contact_telephone: '',
        process_commande: '',
        franco: '',
        minimum_commande: '',
        delai_paiement: '',
        frais_port: '',
        is_active: true,
        supplier_type: 'other',
        is_partner: false,
        capabilities: []
      });
    }
    setShowDialog(true);
    resetDirty();
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingPartner(null);
    resetDirty();
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleImageUpload = async (file, type) => {
    if (!file) return;
    
    const setUploading = type === 'logo' ? setUploadingLogo : setUploadingBanner;
    setUploading(true);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await api.post('/upload', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const imageUrl = response.data.url;
      setFormData(prev => ({ ...prev, [type]: imageUrl }));
      toast.success(`${type === 'logo' ? 'Logo' : 'Bannière'} uploadé`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  // ============================================
  // GESTION MULTI-CONTACTS
  // ============================================
  const addContact = () => {
    const newContact = {
      id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
      nom: '',
      email: '',
      telephone: '',
      fonction: ''
    };
    setFormData(prev => ({
      ...prev,
      contacts: [...prev.contacts, newContact]
    }));
  };

  const removeContact = (contactId) => {
    setFormData(prev => ({
      ...prev,
      contacts: prev.contacts.filter(c => c.id !== contactId)
    }));
  };

  const updateContact = (contactId, field, value) => {
    setFormData(prev => ({
      ...prev,
      contacts: prev.contacts.map(c => 
        c.id === contactId ? { ...c, [field]: value } : c
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nom.trim()) {
      toast.error('Le nom du partenaire est requis');
      return;
    }
    
    // Validation contacts
    const invalidContact = formData.contacts.find(c => !c.nom || !c.nom.trim());
    if (invalidContact) {
      toast.error('Chaque contact doit avoir un nom');
      return;
    }

    try {
      // Nettoyer les contacts
      const cleanedContacts = formData.contacts.map(c => ({
        id: c.id,
        nom: c.nom?.trim() || '',
        email: c.email?.trim() || null,
        telephone: c.telephone?.trim() || null,
        fonction: c.fonction?.trim() || null
      }));
      
      const submitData = {
        nom: formData.nom,
        slug: formData.slug || generateSlug(formData.nom),
        description: formData.description,
        logo: formData.logo,
        banniere: formData.banniere,
        conditions_achat: formData.conditions_achat,
        contacts: cleanedContacts,
        contact_email: cleanedContacts[0]?.email || null,
        contact_telephone: cleanedContacts[0]?.telephone || null,
        process_commande: formData.process_commande,
        franco: formData.franco ? parseFloat(formData.franco) : null,
        minimum_commande: formData.minimum_commande ? parseFloat(formData.minimum_commande) : null,
        delai_paiement: formData.delai_paiement || null,
        frais_port: formData.frais_port || null,
        is_active: formData.is_active,
        supplier_type: formData.supplier_type || 'other',
        is_partner: formData.is_partner === true,
        capabilities: formData.capabilities || [],
        shipping_rules: (formData.shipping_rules || []).filter(r => r.zone),
        payment_terms: {
          mode_paiement: formData.payment_terms?.mode_paiement || null,
          delai_jours: formData.payment_terms?.delai_jours ? parseInt(formData.payment_terms.delai_jours) : null,
          commentaire: formData.payment_terms?.commentaire || null,
          escompte_percent: formData.payment_terms?.escompte_percent ? parseFloat(formData.payment_terms.escompte_percent) : null,
          encours_max: formData.payment_terms?.encours_max ? parseFloat(formData.payment_terms.encours_max) : null,
        },
        precommandes_autorisees: formData.precommandes_autorisees === true,
        rupture_commandable: formData.rupture_commandable === true,
        livraison_partielle: formData.livraison_partielle === true,
      };

      if (editingPartner) {
        await api.put(`/partners/${editingPartner.id}`, submitData);
        toast.success('Partenaire mis à jour');
      } else {
        await api.post('/partners', submitData);
        toast.success('Partenaire créé');
      }
      
      handleCloseDialog();
      loadPartners();
    } catch (error) {
      console.error('Error saving partner:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (partnerId) => {
    try {
      await api.delete(`/partners/${partnerId}`);
      toast.success('Partenaire supprimé');
      setDeleteConfirm(null);
      loadPartners();
    } catch (error) {
      console.error('Error deleting partner:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleToggleActive = async (partner) => {
    try {
      await api.put(`/partners/${partner.id}`, { is_active: !partner.is_active });
      toast.success(partner.is_active ? 'Partenaire désactivé' : 'Partenaire activé');
      loadPartners();
    } catch (error) {
      console.error('Error toggling partner:', error);
      toast.error('Erreur lors de la modification');
    }
  };

  // Tri alphabétique A→Z + filtrage par recherche + filtre par type
  const filteredPartners = partners
    .slice()
    .sort((a, b) => {
      const nameA = (a.nom || a.name || '').toLowerCase().trim();
      const nameB = (b.nom || b.name || '').toLowerCase().trim();
      return nameA.localeCompare(nameB, 'fr', { sensitivity: 'base' });
    })
    .filter(partner => {
      // Filtre par recherche texte
      const matchesSearch = !searchQuery || 
        partner.nom?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        partner.description?.toLowerCase().includes(searchQuery.toLowerCase());
      
      // Filtre par type — compatible capabilities
      const matchesType = typeFilter === 'all' || 
        (partner.capabilities || []).includes(typeFilter) ||
        (partner.supplier_type || 'other') === typeFilter;
      
      return matchesSearch && matchesType;
    });

  const activeCount = partners.filter(p => p.is_active !== false).length;
  const inactiveCount = partners.filter(p => p.is_active === false).length;
  const distributorCount = partners.filter(p => (p.capabilities || []).includes('distributor') || (p.supplier_type || 'other') === 'distributor').length;
  const publisherCount = partners.filter(p => (p.capabilities || []).includes('publisher') || (p.supplier_type || 'other') === 'publisher').length;
  const partnerCommercialCount = partners.filter(p => p.is_partner === true).length;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Bodoni Moda, serif' }} data-testid="admin-partners-title">
            Gestion des Partenaires
          </h1>
          <p className="text-muted-foreground mt-1">
            Gérez les distributeurs et partenaires commerciaux
          </p>
          <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
            <span className="inline-block w-2 h-2 bg-green-500 rounded-full"></span>
            Synchronisation automatique avec les pages partenaires CMS
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="create-partner-button">
          <Plus className="h-4 w-4 mr-2" />
          Nouveau Partenaire
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 bg-primary/10 rounded-lg">
              <Building2 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total</p>
              <p className="text-xl font-bold">{partners.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 bg-blue-50 rounded-lg">
              <Building2 className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Distributeurs</p>
              <p className="text-xl font-bold text-blue-600">{distributorCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 bg-purple-50 rounded-lg">
              <Gamepad2 className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Éditeurs</p>
              <p className="text-xl font-bold text-purple-600">{publisherCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 bg-amber-50 rounded-lg">
              <RosetteIcon className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Partenaires</p>
              <p className="text-xl font-bold text-amber-500">{partnerCommercialCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 bg-green-50 rounded-lg">
              <ToggleRight className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Actifs</p>
              <p className="text-xl font-bold text-green-600">{activeCount}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search + Filters */}
      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher un partenaire..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-partners"
          />
        </div>
        <div className="flex gap-2 flex-wrap" data-testid="type-filter-buttons">
          {[
            { value: 'all', label: 'Tous' },
            { value: 'distributor', label: 'Distributeurs' },
            { value: 'publisher', label: 'Éditeurs' },
            { value: 'other', label: 'Autres' }
          ].map(({ value, label }) => (
            <Button
              key={value}
              variant={typeFilter === value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setTypeFilter(value)}
              data-testid={`filter-type-${value}`}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>

      {/* Partners Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPartners.map((partner) => (
          <Card 
            key={partner.id} 
            className={`relative overflow-hidden transition-all ${partner.is_active === false ? 'opacity-60' : ''}`}
            data-testid={`partner-card-${partner.id}`}
          >
            {/* Logo Container */}
            <div 
              className="bg-gradient-to-br from-gray-50 to-gray-100 relative flex items-center justify-center overflow-hidden"
              style={{ height: '140px' }}
            >
              {partner.logo ? (
                <div className="w-full h-full flex items-center justify-center p-5">
                  <img 
                    src={partner.logo} 
                    alt={partner.nom}
                    className="max-w-full max-h-full object-contain"
                    style={{ width: 'auto', height: 'auto', maxWidth: '80%', maxHeight: '100px' }}
                  />
                </div>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                  <Building2 className="h-12 w-12 mb-2" />
                  <span className="text-xs">Pas de logo</span>
                </div>
              )}
              
              {/* Status Badge */}
              <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium ${
                partner.is_active !== false 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {partner.is_active !== false ? 'Actif' : 'Inactif'}
              </div>
            </div>

            <CardContent className="p-4">
              <h3 className="font-semibold text-lg mb-1">{partner.nom}</h3>
              <p className="text-xs text-muted-foreground font-mono mb-2" data-testid={`partner-id-${partner.id}`}>
                ID : {partner.id}
              </p>
              
              {/* Badges supplier_type + is_partner — Composant mutualisé */}
              <PartnerBadges
                supplierType={partner.supplier_type || 'other'}
                capabilities={partner.capabilities}
                isPartner={partner.is_partner === true}
                size="sm"
                className="mb-2"
              />

              {/* P3 — Badge d'état d'accès Partner */}
              <div className="mb-2">
                {isPartnerClickable(partner) ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-[10px] font-medium" data-testid={`access-badge-${partner.id}`}>
                    <User className="h-2.5 w-2.5" />
                    {getPartnerAccessLabel(partner)}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-50 text-gray-500 border border-gray-200 rounded-full text-[10px] font-medium" data-testid={`access-badge-${partner.id}`}>
                    {PARTNER_LABEL_INACTIVE}
                  </span>
                )}
              </div>

              {partner.description && (
                <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                  {partner.description}
                </p>
              )}

              {/* Info pills */}
              <div className="flex flex-wrap gap-2 mb-3">
                {partner.franco && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs">
                    <Euro className="h-3 w-3" />
                    Franco: {partner.franco}€
                  </span>
                )}
                {partner.minimum_commande && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded-full text-xs">
                    <Euro className="h-3 w-3" />
                    Min: {partner.minimum_commande}€
                  </span>
                )}
              </div>

              {/* Contacts display */}
              {(partner.contacts?.length > 0 || partner.contact_email || partner.contact_telephone) && (
                <div className="mb-3 space-y-1">
                  {partner.contacts?.length > 0 ? (
                    partner.contacts.slice(0, 2).map((contact, idx) => (
                      <div key={contact.id || idx} className="flex items-center gap-2 text-xs text-gray-600">
                        <User className="h-3 w-3 text-gray-400" />
                        <span className="font-medium">{contact.nom}</span>
                        {contact.fonction && (
                          <span className="text-gray-400">({contact.fonction})</span>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {partner.contact_email && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
                          <Mail className="h-3 w-3" />
                          Email
                        </span>
                      )}
                      {partner.contact_telephone && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
                          <Phone className="h-3 w-3" />
                          Tél
                        </span>
                      )}
                    </div>
                  )}
                  {partner.contacts?.length > 2 && (
                    <span className="text-xs text-gray-400">
                      +{partner.contacts.length - 2} autre(s) contact(s)
                    </span>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-2 pt-3 border-t">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex-1"
                  onClick={() => handleOpenDialog(partner)}
                  data-testid={`edit-partner-${partner.id}`}
                >
                  <Pencil className="h-4 w-4 mr-1" />
                  Éditer
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const email = window.prompt(`Inviter un utilisateur pour "${partner.nom}".\nSaisissez l'email :`);
                    if (email && email.includes('@')) {
                      api.post('/partner-invitations', { partner_id: partner.id, email: email.trim().toLowerCase() })
                        .then(res => {
                          toast.success(`Invitation envoyée à ${email}`);
                          if (res.data?.invite_url) {
                            navigator.clipboard?.writeText(res.data.invite_url);
                            toast.success('Lien copié dans le presse-papier');
                          }
                        })
                        .catch(err => toast.error(err.response?.data?.detail || 'Erreur'));
                    }
                  }}
                  title={isPartnerClickable(partner) ? 'Gérer les accès partenaire' : 'Inviter un utilisateur'}
                  data-testid={`invite-partner-${partner.id}`}
                >
                  <Mail className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleToggleActive(partner)}
                  title={partner.is_active !== false ? 'Désactiver' : 'Activer'}
                >
                  {partner.is_active !== false ? (
                    <ToggleRight className="h-4 w-4 text-green-600" />
                  ) : (
                    <ToggleLeft className="h-4 w-4 text-gray-400" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDeleteConfirm(partner)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid={`delete-partner-${partner.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredPartners.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <p className="text-muted-foreground">
            {searchQuery ? 'Aucun partenaire trouvé' : 'Aucun partenaire créé'}
          </p>
          {!searchQuery && (
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => handleOpenDialog()}
            >
              <Plus className="h-4 w-4 mr-2" />
              Créer le premier partenaire
            </Button>
          )}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <GuardedDialog open={showDialog} onOpenChange={(open) => { if (!open) handleCloseDialog(); }} isDirty={hasUnsavedChanges} className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{editingPartner ? 'Modifier le partenaire' : 'Nouveau partenaire'}</span>
              {editingPartner?.id && (
                <button
                  onClick={() => window.open(`/dashboard/partner-preview/${editingPartner.id}`, '_blank')}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-full transition-colors"
                  data-testid="view-as-retailer-btn"
                >
                  <ExternalLink className="h-3 w-3" />
                  Voir comme Retailer
                </button>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} onChange={() => setDirty()} className="space-y-6">
            {/* Informations de base */}
            <div className="space-y-4">
              <h3 className="font-medium flex items-center gap-2 text-sm text-muted-foreground border-b pb-2">
                <Building2 className="h-4 w-4" />
                Informations générales
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 md:col-span-1">
                  <Label htmlFor="nom">Nom du partenaire *</Label>
                  <Input
                    id="nom"
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    placeholder="Asmodée France"
                    required
                    data-testid="input-partner-nom"
                  />
                </div>
                <div className="col-span-2 md:col-span-1">
                  <Label htmlFor="slug">Slug (URL)</Label>
                  <Input
                    id="slug"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    placeholder="asmodee-france"
                    data-testid="input-partner-slug"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Généré automatiquement si vide
                  </p>
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Description du partenaire..."
                  rows={3}
                  data-testid="input-partner-description"
                />
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label htmlFor="is_active">Partenaire actif</Label>
              </div>

              {/* Classification fournisseur V4 — Capabilities */}
              <div className="space-y-3">
                <Label className="font-semibold">Rôles métier (capabilities)</Label>
                <div className="flex flex-wrap gap-3" data-testid="capabilities-checkboxes">
                  {[
                    { value: 'distributor', label: 'Distributeur', icon: Building2, color: 'text-blue-600' },
                    { value: 'publisher', label: 'Éditeur', icon: Gamepad2, color: 'text-purple-600' },
                    { value: 'other', label: 'Autre', icon: Settings2, color: 'text-gray-500' }
                  ].map(({ value, label, icon: Icon, color }) => {
                    const checked = (formData.capabilities || []).includes(value);
                    return (
                      <label
                        key={value}
                        className={`flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer transition-all ${checked ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200 hover:border-gray-300'}`}
                        data-testid={`cap-checkbox-${value}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const caps = formData.capabilities || [];
                            let newCaps = checked
                              ? caps.filter(c => c !== value)
                              : [...caps, value];
                            // Garantie ≥1 capability : si vide → ["other"]
                            if (newCaps.length === 0) newCaps = ['other'];
                            // Sync supplier_type from primary capability
                            const primary = newCaps.find(c => ['distributor', 'publisher'].includes(c)) || 'other';
                            setFormData({ ...formData, capabilities: newCaps, supplier_type: primary });
                          }}
                          className="rounded border-gray-300"
                        />
                        <Icon className={`h-4 w-4 ${color}`} />
                        <span className="text-sm font-medium">{label}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground">
                  Un partenaire peut cumuler plusieurs rôles. Au minimum 1 rôle requis.
                </p>
              </div>

              <div className="flex items-end">
                <div className="flex items-center gap-2 pb-1">
                  <Switch
                      id="is_partner"
                      checked={formData.is_partner}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_partner: checked })}
                      data-testid="switch-is-partner"
                    />
                    <Label htmlFor="is_partner" className="flex items-center gap-1.5">
                      <RosetteIcon className="w-5 h-5" />
                      Partenaire commercial
                    </Label>
                  </div>
                </div>
            </div>

            {/* Visuels */}
            <div className="space-y-4">
              <h3 className="font-medium flex items-center gap-2 text-sm text-muted-foreground border-b pb-2">
                <Image className="h-4 w-4" />
                Visuels
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="logo">Logo</Label>
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        id="logo"
                        value={formData.logo}
                        onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                        placeholder="https://..."
                        className="flex-1"
                        data-testid="input-partner-logo"
                      />
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleImageUpload(e.target.files?.[0], 'logo')}
                        />
                        <Button type="button" variant="outline" size="icon" disabled={uploadingLogo} asChild>
                          <span>
                            <Upload className={`h-4 w-4 ${uploadingLogo ? 'animate-pulse' : ''}`} />
                          </span>
                        </Button>
                      </label>
                    </div>
                    {formData.logo && (
                      <div className="p-2 bg-gray-50 rounded border">
                        <img src={formData.logo} alt="Logo preview" className="max-h-16 object-contain mx-auto" />
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <Label htmlFor="banniere">Bannière</Label>
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        id="banniere"
                        value={formData.banniere}
                        onChange={(e) => setFormData({ ...formData, banniere: e.target.value })}
                        placeholder="https://..."
                        className="flex-1"
                        data-testid="input-partner-banniere"
                      />
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleImageUpload(e.target.files?.[0], 'banniere')}
                        />
                        <Button type="button" variant="outline" size="icon" disabled={uploadingBanner} asChild>
                          <span>
                            <Upload className={`h-4 w-4 ${uploadingBanner ? 'animate-pulse' : ''}`} />
                          </span>
                        </Button>
                      </label>
                    </div>
                    {formData.banniere && (
                      <div className="p-2 bg-gray-50 rounded border">
                        <img src={formData.banniere} alt="Bannière preview" className="max-h-16 w-full object-cover rounded" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* ============================================ */}
            {/* 3. Conditions de livraison */}
            {/* ============================================ */}
            <div className="space-y-4">
              <h3 className="font-medium flex items-center gap-2 text-sm text-muted-foreground border-b pb-2">
                <MapPin className="h-4 w-4" />
                Conditions de livraison
              </h3>
              <p className="text-xs text-muted-foreground">
                Donn&eacute;es structur&eacute;es pour le calcul automatique du franco et des frais de port par zone.
              </p>
              
              {(formData.shipping_rules || []).map((rule, ri) => (
                <div key={rule.id || ri} className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2" data-testid={`shipping-rule-${ri}`}>
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="text-xs">Zone {ri + 1}</Badge>
                    <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500"
                      onClick={() => {
                        const rules = [...(formData.shipping_rules || [])];
                        rules.splice(ri, 1);
                        setFormData({ ...formData, shipping_rules: rules });
                      }}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <div>
                      <Label className="text-[10px]">Zone *</Label>
                      <Select
                        value={rule.zone}
                        onValueChange={(value) => {
                          const rules = [...(formData.shipping_rules || [])];
                          rules[ri] = { ...rules[ri], zone: value };
                          setFormData({ ...formData, shipping_rules: rules });
                        }}
                      >
                        <SelectTrigger className="h-8 text-xs" data-testid={`shipping-zone-${ri}`}>
                          <SelectValue placeholder="Choisir..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="france_metropolitaine">France m&eacute;tropolitaine</SelectItem>
                          <SelectItem value="belgique">Belgique</SelectItem>
                          <SelectItem value="suisse">Suisse</SelectItem>
                          <SelectItem value="luxembourg">Luxembourg</SelectItem>
                          <SelectItem value="dom_tom">DOM-TOM</SelectItem>
                          <SelectItem value="europe">Europe</SelectItem>
                          <SelectItem value="international">International</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[10px]">Franco (&euro;)</Label>
                      <Input type="number" step="0.01" className="h-8 text-xs"
                        value={rule.franco ?? ''}
                        onChange={(e) => {
                          const rules = [...(formData.shipping_rules || [])];
                          rules[ri] = { ...rules[ri], franco: e.target.value ? parseFloat(e.target.value) : null };
                          setFormData({ ...formData, shipping_rules: rules });
                        }}
                        placeholder="350"
                        data-testid={`shipping-franco-${ri}`} />
                    </div>
                    <div>
                      <Label className="text-[10px]">Min. commande (&euro;)</Label>
                      <Input type="number" step="0.01" className="h-8 text-xs"
                        value={rule.minimum_commande ?? ''}
                        onChange={(e) => {
                          const rules = [...(formData.shipping_rules || [])];
                          rules[ri] = { ...rules[ri], minimum_commande: e.target.value ? parseFloat(e.target.value) : null };
                          setFormData({ ...formData, shipping_rules: rules });
                        }}
                        placeholder="150"
                        data-testid={`shipping-min-${ri}`} />
                    </div>
                    <div>
                      <Label className="text-[10px]">Frais de port fixe (&euro;)</Label>
                      <Input type="number" step="0.01" className="h-8 text-xs"
                        value={rule.frais_port_fixe ?? ''}
                        onChange={(e) => {
                          const rules = [...(formData.shipping_rules || [])];
                          rules[ri] = { ...rules[ri], frais_port_fixe: e.target.value ? parseFloat(e.target.value) : null };
                          setFormData({ ...formData, shipping_rules: rules });
                        }}
                        placeholder="12.50"
                        data-testid={`shipping-fixe-${ri}`} />
                    </div>
                    <div>
                      <Label className="text-[10px]">Frais de port % du total</Label>
                      <Input type="number" step="0.1" className="h-8 text-xs"
                        value={rule.frais_port_percent ?? ''}
                        onChange={(e) => {
                          const rules = [...(formData.shipping_rules || [])];
                          rules[ri] = { ...rules[ri], frais_port_percent: e.target.value ? parseFloat(e.target.value) : null };
                          setFormData({ ...formData, shipping_rules: rules });
                        }}
                        placeholder="3.5"
                        data-testid={`shipping-pct-${ri}`} />
                    </div>
                    <div>
                      <Label className="text-[10px]">D&eacute;lai exp&eacute;dition moyen* (jours)</Label>
                      <Input type="number" min="0" step="1" className="h-8 text-xs"
                        value={rule.delai_expedition_jours ?? ''}
                        onChange={(e) => {
                          const rules = [...(formData.shipping_rules || [])];
                          rules[ri] = { ...rules[ri], delai_expedition_jours: e.target.value ? parseInt(e.target.value) : null };
                          setFormData({ ...formData, shipping_rules: rules });
                        }}
                        placeholder="3"
                        data-testid={`shipping-delai-${ri}`} />
                    </div>
                  </div>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" className="text-xs" onClick={() => {
                const newRule = { id: Date.now().toString(), zone: '', minimum_commande: null, franco: null, frais_port_fixe: null, frais_port_percent: null, delai_expedition_jours: null };
                setFormData({ ...formData, shipping_rules: [...(formData.shipping_rules || []), newRule] });
              }} data-testid="add-shipping-rule">
                <Plus className="h-3 w-3 mr-1" /> Ajouter une zone
              </Button>
            </div>

            {/* ============================================ */}
            {/* 4. Indicateurs de distribution */}
            {/* ============================================ */}
            <div className="space-y-4">
              <h3 className="font-medium flex items-center gap-2 text-sm text-muted-foreground border-b pb-2">
                <PackageCheck className="h-4 w-4" />
                Indicateurs de distribution
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex items-center gap-2.5 p-3 rounded-lg border border-slate-200 bg-slate-50">
                  <Switch id="precommandes" checked={formData.precommandes_autorisees}
                    onCheckedChange={(v) => setFormData({ ...formData, precommandes_autorisees: v })}
                    data-testid="switch-precommandes" />
                  <Label htmlFor="precommandes" className="text-xs font-medium cursor-pointer">
                    Pr&eacute;commandes
                  </Label>
                </div>
                <div className="flex items-center gap-2.5 p-3 rounded-lg border border-slate-200 bg-slate-50">
                  <Switch id="rupture" checked={formData.rupture_commandable}
                    onCheckedChange={(v) => setFormData({ ...formData, rupture_commandable: v })}
                    data-testid="switch-rupture" />
                  <Label htmlFor="rupture" className="text-xs font-medium cursor-pointer">
                    Rupture commandable
                  </Label>
                </div>
                <div className="flex items-center gap-2.5 p-3 rounded-lg border border-slate-200 bg-slate-50">
                  <Switch id="partielle" checked={formData.livraison_partielle}
                    onCheckedChange={(v) => setFormData({ ...formData, livraison_partielle: v })}
                    data-testid="switch-livraison-partielle" />
                  <Label htmlFor="partielle" className="text-xs font-medium cursor-pointer">
                    Livraison partielle
                  </Label>
                </div>
              </div>
            </div>

            {/* ============================================ */}
            {/* 5. Conditions d'Achats */}
            {/* ============================================ */}
            <div className="space-y-4">
              <h3 className="font-medium flex items-center gap-2 text-sm text-muted-foreground border-b pb-2">
                <ShoppingCart className="h-4 w-4" />
                Conditions d&apos;Achats
              </h3>
              <div>
                <Textarea
                  id="conditions_achat"
                  value={formData.conditions_achat}
                  onChange={(e) => setFormData({ ...formData, conditions_achat: e.target.value })}
                  placeholder="D&eacute;taillez les conditions d'achat..."
                  rows={3}
                  data-testid="input-partner-conditions-achat"
                />
              </div>
            </div>

            {/* ============================================ */}
            {/* 6. Conditions de Paiement */}
            {/* ============================================ */}
            <div className="space-y-4">
              <h3 className="font-medium flex items-center gap-2 text-sm text-muted-foreground border-b pb-2">
                <Euro className="h-4 w-4" />
                Conditions de Paiement
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Mode de paiement</Label>
                  <Input
                    value={formData.payment_terms?.mode_paiement ?? ''}
                    onChange={(e) => setFormData({ ...formData, payment_terms: { ...formData.payment_terms, mode_paiement: e.target.value } })}
                    placeholder="Ex: Virement, Ch&egrave;que, Pr&eacute;l&egrave;vement..."
                    className="max-w-xs"
                    data-testid="input-payment-mode" />
                </div>
                <div>
                  <Label className="text-xs">D&eacute;lai paiement (jours)</Label>
                  <Input type="number" min="0" step="1"
                    value={formData.payment_terms?.delai_jours ?? ''}
                    onChange={(e) => setFormData({ ...formData, payment_terms: { ...formData.payment_terms, delai_jours: e.target.value } })}
                    placeholder="30" className="max-w-xs"
                    data-testid="input-payment-delai" />
                </div>
                <div>
                  <Label className="text-xs">Commentaire paiement</Label>
                  <Input
                    value={formData.payment_terms?.commentaire ?? ''}
                    onChange={(e) => setFormData({ ...formData, payment_terms: { ...formData.payment_terms, commentaire: e.target.value } })}
                    placeholder="Ex: Fin de mois, date de facture..."
                    className="max-w-xs"
                    data-testid="input-payment-commentaire" />
                </div>
                <div>
                  <Label className="text-xs">Escompte %</Label>
                  <Input type="number" step="0.1"
                    value={formData.payment_terms?.escompte_percent ?? ''}
                    onChange={(e) => setFormData({ ...formData, payment_terms: { ...formData.payment_terms, escompte_percent: e.target.value ? parseFloat(e.target.value) : null } })}
                    placeholder="2.0" className="max-w-xs"
                    data-testid="input-payment-escompte" />
                </div>
                <div>
                  <Label className="text-xs">Encours max (&euro;)</Label>
                  <Input type="number" step="100"
                    value={formData.payment_terms?.encours_max ?? ''}
                    onChange={(e) => setFormData({ ...formData, payment_terms: { ...formData.payment_terms, encours_max: e.target.value ? parseFloat(e.target.value) : null } })}
                    placeholder="15000" className="max-w-xs"
                    data-testid="input-payment-encours" />
                </div>
              </div>
            </div>

            {/* ============================================ */}
            {/* 7. Process de Commande */}
            {/* ============================================ */}
            <div className="space-y-4">
              <h3 className="font-medium flex items-center gap-2 text-sm text-muted-foreground border-b pb-2">
                <FileText className="h-4 w-4" />
                Process de Commande
              </h3>
              <div>
                <Textarea
                  id="process_commande"
                  value={formData.process_commande}
                  onChange={(e) => setFormData({ ...formData, process_commande: e.target.value })}
                  placeholder="D&eacute;crivez le processus de commande..."
                  rows={3}
                  data-testid="input-partner-process-commande"
                />
              </div>
            </div>

            {/* ============================================ */}
            {/* 8. Contacts */}
            {/* ============================================ */}
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b pb-2">
                <h3 className="font-medium flex items-center gap-2 text-sm text-muted-foreground">
                  <User className="h-4 w-4" />
                  Contacts
                </h3>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={addContact}
                  data-testid="admin-add-contact-button"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Ajouter
                </Button>
              </div>
              
              {formData.contacts.length === 0 ? (
                <div className="text-center py-4 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                  <User className="h-6 w-6 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-muted-foreground">Aucun contact</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {formData.contacts.map((contact, index) => (
                    <div 
                      key={contact.id} 
                      className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                      data-testid={`admin-contact-${index}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="outline" className="text-xs">Contact {index + 1}</Badge>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeContact(contact.id)}
                          className="text-red-500 hover:text-red-700 h-6 w-6 p-0"
                          data-testid={`admin-remove-contact-${index}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Nom *</Label>
                          <Input
                            value={contact.nom}
                            onChange={(e) => updateContact(contact.id, 'nom', e.target.value)}
                            placeholder="Prénom Nom"
                            className="h-8 text-sm"
                            data-testid={`admin-contact-nom-${index}`}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Fonction</Label>
                          <Input
                            value={contact.fonction || ''}
                            onChange={(e) => updateContact(contact.id, 'fonction', e.target.value)}
                            placeholder="Ex: Directeur"
                            className="h-8 text-sm"
                            data-testid={`admin-contact-fonction-${index}`}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Email</Label>
                          <Input
                            type="email"
                            value={contact.email || ''}
                            onChange={(e) => updateContact(contact.id, 'email', e.target.value)}
                            placeholder="email@example.com"
                            className="h-8 text-sm"
                            data-testid={`admin-contact-email-${index}`}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Téléphone</Label>
                          <Input
                            value={contact.telephone || ''}
                            onChange={(e) => updateContact(contact.id, 'telephone', e.target.value)}
                            placeholder="01 23 45 67 89"
                            className="h-8 text-sm"
                            data-testid={`admin-contact-telephone-${index}`}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Annuler
              </Button>
              <Button type="submit" data-testid="submit-partner">
                {editingPartner ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
      </GuardedDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce partenaire ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le partenaire "{deleteConfirm?.nom}" sera définitivement supprimé.
              Les produits liés à ce partenaire perdront leur association.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(deleteConfirm?.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </DashboardLayout>
  );
};

export default AdminPartners;
